import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import DrugCard from '../components/DrugCard';
import apiService from '../services/api';

function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState('ingredient');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rxcuiInput, setRxcuiInput] = useState('');

  useEffect(() => {
    const query = searchParams.get('q');
    if (query) {
      performSearch(query, 'ingredient');
    }
  }, [searchParams]);

  const performSearch = async (query, mode = searchMode) => {
    setLoading(true);
    setError(null);
    setResults([]);

    try {
      let response;
      
      if (mode === 'ingredient') {
        response = await apiService.searchByIngredient(query);
        // Fetch counts for each collection
        const resultsWithCounts = await Promise.all(
          response.data.results.map(async (result) => {
            try {
              const details = await apiService.getDrugDetails(result.id);
              return { ...result, counts: details.data.counts };
            } catch {
              return result;
            }
          })
        );
        setResults(resultsWithCounts);
      } else if (mode === 'rxcui') {
        response = await apiService.searchByRxCUI(query);
        // Fetch counts for each collection
        const resultsWithCounts = await Promise.all(
          response.data.results.map(async (result) => {
            try {
              const details = await apiService.getDrugDetails(result.id);
              return { ...result, counts: details.data.counts };
            } catch {
              return result;
            }
          })
        );
        setResults(resultsWithCounts);
      } else if (mode === 'product') {
        response = await apiService.searchByProduct(query);
        setResults(response.data.results);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchParams({ q: query });
  };

  const handleRxcuiSearch = (e) => {
    e.preventDefault();
    if (rxcuiInput.trim()) {
      performSearch(rxcuiInput.trim(), 'rxcui');
    }
  };

  return (
    <div className="container">
      <h1 style={{ marginBottom: '2rem' }}>Search Drugs</h1>

      {/* Search Mode Tabs */}
      <div className="tabs">
        <button
          className={`tab ${searchMode === 'ingredient' ? 'active' : ''}`}
          onClick={() => setSearchMode('ingredient')}
        >
          By Ingredient Name
        </button>
        <button
          className={`tab ${searchMode === 'rxcui' ? 'active' : ''}`}
          onClick={() => setSearchMode('rxcui')}
        >
          By RxCUI
        </button>
        <button
          className={`tab ${searchMode === 'product' ? 'active' : ''}`}
          onClick={() => setSearchMode('product')}
        >
          By Product
        </button>
      </div>

      {/* Search Interface */}
      <div className="card">
        {searchMode === 'ingredient' && (
          <SearchBar 
            onSearch={handleSearch}
            placeholder="Search by ingredient name (e.g., amoxicillin)..."
          />
        )}

        {searchMode === 'rxcui' && (
          <form onSubmit={handleRxcuiSearch} style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              className="input"
              placeholder="Enter RxCUI (e.g., 723 for amoxicillin)..."
              value={rxcuiInput}
              onChange={(e) => setRxcuiInput(e.target.value)}
            />
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </form>
        )}

        {searchMode === 'product' && (
          <SearchBar 
            onSearch={(query) => performSearch(query, 'product')}
            placeholder="Search by product name (e.g., Augmentin)..."
          />
        )}
      </div>

      {/* Results */}
      {loading && <div className="loading">Searching...</div>}
      
      {error && (
        <div className="error">
          Error: {error}
        </div>
      )}

      {!loading && !error && results.length > 0 && (
        <div>
          <h2 style={{ margin: '2rem 0 1rem' }}>
            Results ({results.length})
          </h2>

          {searchMode === 'product' ? (
            <div>
              {results.map((result, index) => (
                <div key={index} className="card">
                  <h3 style={{ marginBottom: '0.5rem' }}>
                    📦 {result.product_name}
                  </h3>
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: 'var(--text-secondary)', 
                    marginBottom: '1rem' 
                  }}>
                    Ingredients: {result.collection_label}
                  </p>
                  <a 
                    href={`/drug/${result.collection_id}`} 
                    className="btn btn-primary"
                  >
                    View Drug Details
                  </a>
                </div>
              ))}
            </div>
          ) : (
            results.map((result, index) => (
              <DrugCard key={index} drug={result} />
            ))
          )}
        </div>
      )}

      {!loading && !error && results.length === 0 && searchParams.get('q') && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <p style={{ color: 'var(--text-secondary)' }}>
            No results found for "{searchParams.get('q')}"
          </p>
        </div>
      )}
    </div>
  );
}

export default Search;
